/**
 * Parser de estado de cuenta BBVA empresarial (PDF texto extraíble).
 *
 * Formato de la tabla "Detalle de Movimientos Realizados":
 *   FECHA       COD. DESCRIPCIÓN  REFERENCIA  CARGOS  ABONOS  SALDO_OPER SALDO_LIQ
 *   OPER  LIQ
 *   02/ENE 01/ENE  G30 RECIBO NO.   Ref. ...   739.40           22,260.72  22,013.74
 *
 * Las filas pueden ocupar varias líneas — la primera tiene la fecha y el
 * monto, las siguientes son continuación de la referencia.
 *
 * Códigos importantes:
 *   T17 SPEI ENVIADO   → transferencia saliente
 *   T20 SPEI RECIBIDO  → transferencia entrante
 *   N06 PAGO CUENTA DE TERCERO → pago saliente
 *   P14 KONFIO         → pago a TDC Konfio (transfer interno)
 *   P14 SAT            → pago al SAT (impuestos)
 *   A15 PAYU/...       → cargo/compra (gasto)
 *   S39 SERV BANCA / S40 IVA COM → comisiones bancarias
 *   G30 RECIBO         → recibo (luz, agua, etc)
 *   BNET               → transferencia electrónica
 */
import type { BulkParseResult, BulkParsedRow } from "./types";

const MES_MAP: Record<string, number> = {
  ENE: 1,
  FEB: 2,
  MAR: 3,
  ABR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AGO: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DIC: 12,
};

const RFC_PROPIO = "NOB230522884";

const MONTH_NAME_MAP: Record<string, number> = {
  enero: 1,
  febrero: 2,
  marzo: 3,
  abril: 4,
  mayo: 5,
  junio: 6,
  julio: 7,
  agosto: 8,
  septiembre: 9,
  octubre: 10,
  noviembre: 11,
  diciembre: 12,
};

/**
 * Punto de entrada — detecta la variante (oficial mensual vs provisional
 * "Mes actual" desde la web) y delega al parser correspondiente.
 */
export function parseBBVA(text: string): BulkParseResult {
  if (/Mes\s+actual/i.test(text) || /Cuenta\s+con\s*\/\s*sin\s+chequera/i.test(text)) {
    return parseBBVAProvisional(text);
  }
  return parseBBVAOficial(text);
}

function parseBBVAOficial(text: string): BulkParseResult {
  const errors: string[] = [];

  // Periodo: "DEL 01/01/2026 AL 31/01/2026"
  const periodMatch = text.match(
    /DEL\s+(\d{2})\/(\d{2})\/(\d{4})\s+AL\s+(\d{2})\/(\d{2})\/(\d{4})/,
  );
  const year = periodMatch ? parseInt(periodMatch[3]!, 10) : new Date().getFullYear();
  const periodStart = periodMatch
    ? `${periodMatch[3]}-${periodMatch[2]}-${periodMatch[1]}`
    : undefined;
  const periodEnd = periodMatch
    ? `${periodMatch[6]}-${periodMatch[5]}-${periodMatch[4]}`
    : undefined;

  // No. de cuenta
  const accountMatch = text.match(/No\.\s*de\s*Cuenta[\s\S]*?(\d{10,})/i);
  const accountHint = accountMatch?.[1];

  // Saldo final (No. operación final / saldo de operación final)
  const closingMatch = text.match(/Saldo\s+de\s+Operaci[oó]n\s+Final[^\d-]*([-\d,]+\.?\d{0,2})/i);
  const closingBalanceCents = closingMatch ? parsePesos(closingMatch[1]!) : undefined;

  // Tokenizar: cada movimiento empieza con "DD/MES DD/MES CODE DESCRIPCION ..."
  // Localizamos las líneas que empiezan con esa firma. Las siguientes hasta la
  // próxima firma son continuación.
  const lines = text.split(/\n/).map((l) => l.trim());
  const rowRegex =
    /^(\d{2})\/(ENE|FEB|MAR|ABR|MAY|JUN|JUL|AGO|SEP|OCT|NOV|DIC)\s+(\d{2})\/(ENE|FEB|MAR|ABR|MAY|JUN|JUL|AGO|SEP|OCT|NOV|DIC)\s+([A-Z]\d{2}|[A-Z]{2,4})?\s*(.*)/;

  // Saldo de liquidación inicial — sirve como punto de partida para clasificar
  // cargo/abono por diferencia. Lo extraemos del header del estado.
  const openingMatch = text.match(/Saldo\s+de\s+Liquidaci[oó]n\s+Inicial[^\d-]*([-\d,]+\.\d{2})/i);
  let lastSaldoCents: number | null = openingMatch ? parsePesos(openingMatch[1]!) : null;

  // Primera pasada: parsear todas las filas. Acumulamos también si el kind
  // viene del código (confiable) o por keyword/diff (potencialmente
  // ambiguo). El kind ambiguo se reconciliará en una 2da pasada usando los
  // saldos confirmados por el PDF entre bloques.
  type Partial = {
    row: BulkParsedRow;
    pdfSaldoCents: number | null; // saldo realmente leído del PDF (no proyectado)
    ambiguous: boolean; // true si el kind salió de heurística no confiable
  };
  const partial: Partial[] = [];

  let cur: { dateOper: string; firstLine: string; extras: string[] } | null = null;
  const flushCur = () => {
    if (!cur) return;
    const fullText = [cur.firstLine, ...cur.extras].join(" ");
    const parsed = parseBBVAFullRow(cur.dateOper, fullText, lastSaldoCents);
    if (parsed) {
      partial.push({
        row: parsed.row,
        pdfSaldoCents: parsed.pdfSaldoCents,
        ambiguous: parsed.ambiguous,
      });
      // Mantener tracking del saldo (puede ser confirmado del PDF o
      // proyectado a partir del kind tentativo).
      if (parsed.newSaldoCents !== null) lastSaldoCents = parsed.newSaldoCents;
    } else {
      errors.push(`No pude parsear: ${fullText.slice(0, 120)}`);
    }
    cur = null;
  };

  for (const line of lines) {
    if (!line) continue;
    const m = line.match(rowRegex);
    if (m) {
      flushCur();
      const day = parseInt(m[1]!, 10);
      const monthNum = MES_MAP[m[2]!];
      if (!monthNum) {
        errors.push(`Mes desconocido: ${m[2]}`);
        continue;
      }
      const dateOper = `${year}-${String(monthNum).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      cur = { dateOper, firstLine: line, extras: [] };
    } else if (cur) {
      // Continuación — línea de referencia, no empieza con fecha.
      // Filtra ruido conocido (paginación, "BBVA MEXICO, S.A.", etc.).
      if (
        line.startsWith("Página") ||
        line.startsWith("BBVA MEXICO") ||
        line.startsWith("Av. Paseo")
      ) {
        continue;
      }
      cur.extras.push(line);
    }
  }
  flushCur();

  // Segunda pasada: resolver filas ambiguas usando saldos confirmados por
  // el PDF. Buscamos bloques [saldoConfirmadoPrev … saldoConfirmadoNext]
  // y ajustamos las filas ambiguas para que cuadren la diferencia.
  const openingCents = openingMatch ? parsePesos(openingMatch[1]!) : null;
  if (openingCents !== null) {
    resolveAmbiguous(partial, openingCents);
  }

  // Tercera pasada: cuadrar contra TOTAL IMPORTE ABONOS / CARGOS del
  // resumen del PDF. Cuando el brute force del paso 2 encontró una
  // solución que cuadra el saldo del bloque pero no necesariamente la
  // correcta (N06 ambiguas con varias soluciones válidas), este paso
  // intenta flip de filas ambiguas para igualar el total mensual del
  // PDF — la verdad contable última.
  const totalAbonosMatch = text.match(
    /Dep[oó]sitos\s*\/\s*Abonos\s*\(\s*\+\s*\)\s+\d+\s+([\d,]+\.\d{2})/i,
  );
  const totalCargosMatch = text.match(
    /Retiros\s*\/\s*Cargos\s*\(\s*-\s*\)\s+\d+\s+([\d,]+\.\d{2})/i,
  );
  if (totalAbonosMatch && totalCargosMatch) {
    const expAbonos = parsePesos(totalAbonosMatch[1]!);
    const expCargos = parsePesos(totalCargosMatch[1]!);
    balanceAgainstTotals(partial, expAbonos, expCargos);
  }

  return {
    bank: "bbva",
    accountHint,
    periodStart,
    periodEnd,
    closingBalanceCents,
    rows: partial.map((p) => p.row),
    errors,
  };
}

/**
 * Para cuadrar exacto contra los totales del PDF, busca un subset de
 * filas N06 / ambiguas que al flippear cierren la brecha entre los
 * totales del parser y los del PDF.
 *
 * Estrategia: si el parser tiene MÁS ingresos de los esperados, busca
 * filas marcadas como ingreso cuya suma == exceso y las cambia a gasto
 * (y viceversa). Solo considera filas que fueron ambiguas en el parseo
 * original (porque las clasificadas por código son confiables).
 */
function balanceAgainstTotals(
  partial: Array<{
    row: BulkParsedRow;
    pdfSaldoCents: number | null;
    ambiguous: boolean;
  }>,
  expAbonos: number,
  _expCargos: number,
): void {
  const sumIng = partial
    .filter((p) => p.row.kind === "ingreso")
    .reduce((s, p) => s + p.row.amountCents, 0);
  const excessIngreso = sumIng - expAbonos;
  if (Math.abs(excessIngreso) <= 100) return; // ya cuadra

  // Si excessIngreso > 0: parser tiene más ingresos. Necesitamos
  // flippear filas ingreso → gasto que sumen exactamente excessIngreso.
  // Si excessIngreso < 0: flippear gastos → ingreso por |excessIngreso|.
  const targetDelta = Math.abs(excessIngreso);
  const flipFromKind: "ingreso" | "gasto" = excessIngreso > 0 ? "ingreso" : "gasto";
  const flipToKind: "ingreso" | "gasto" = excessIngreso > 0 ? "gasto" : "ingreso";

  // Solo flippear filas que fueron ambiguas (las que el código BBVA no
  // clasificó con certeza).
  const candidates = partial
    .map((p, i) => ({ idx: i, amt: p.row.amountCents, ambiguous: p.ambiguous }))
    .filter((c) => c.ambiguous && partial[c.idx]!.row.kind === flipFromKind);

  if (candidates.length === 0) return;
  // Buscar subset que sume exactamente targetDelta (±$1).
  // Brute force 2^N hasta 14 candidatos.
  const n = candidates.length;
  if (n > 16) return;
  for (let mask = 1; mask < 1 << n; mask++) {
    let s = 0;
    for (let k = 0; k < n; k++) if (mask & (1 << k)) s += candidates[k]!.amt;
    if (Math.abs(s - targetDelta) <= 100) {
      for (let k = 0; k < n; k++) {
        if (mask & (1 << k)) partial[candidates[k]!.idx]!.row.kind = flipToKind;
      }
      return;
    }
  }
}

/**
 * Resuelve filas ambiguas usando saldos confirmados del PDF como anclas.
 *
 * Para cada bloque [ancla_prev … ancla_next], el diff total debe ser igual
 * a la suma firmada de movimientos. Las filas no-ambiguas tienen kind
 * confiable (vino del código BBVA). Las filas ambiguas se asignan
 * intentando combinaciones hasta cuadrar el diff.
 */
function resolveAmbiguous(
  partial: Array<{
    row: BulkParsedRow;
    pdfSaldoCents: number | null;
    ambiguous: boolean;
  }>,
  openingCents: number,
): void {
  let lastAnchor = openingCents;
  let lastAnchorIdx = -1; // antes del primer movimiento

  const settleBlock = (endIdx: number, endAnchor: number) => {
    const indices: number[] = [];
    for (let j = lastAnchorIdx + 1; j <= endIdx; j++) indices.push(j);
    if (indices.length === 0) return;

    let sumFixed = 0;
    const ambiguousIdx: number[] = [];
    for (const j of indices) {
      const r = partial[j]!;
      if (r.ambiguous) ambiguousIdx.push(j);
      else sumFixed += (r.row.kind === "ingreso" ? 1 : -1) * r.row.amountCents;
    }

    const totalDiff = endAnchor - lastAnchor;
    const required = totalDiff - sumFixed;

    if (ambiguousIdx.length === 0) {
      // Nada que ajustar; si no cuadra es por filas no-ambiguas mal
      // clasificadas (poco común). No tocamos.
      return;
    }

    if (ambiguousIdx.length === 1) {
      const j = ambiguousIdx[0]!;
      const amt = partial[j]!.row.amountCents;
      if (Math.abs(required - amt) <= 100) partial[j]!.row.kind = "ingreso";
      else if (Math.abs(required + amt) <= 100) partial[j]!.row.kind = "gasto";
      return;
    }

    // 2+ ambiguas: brute force 2^N. Generamos TODAS las combinaciones
    // válidas y elegimos la que tenga MÁS gastos (más conservadora para
    // N06 PAGO CUENTA DE TERCERO, que típicamente es saliente). Si varias
    // tienen el mismo número de gastos, preferimos la que asigne ingreso
    // a filas con monto > 50000 (típicamente cobros grandes de clientes).
    const n = ambiguousIdx.length;
    if (n > 14) return;
    let best: number | null = null;
    let bestScore = -Infinity;
    for (let mask = 0; mask < 1 << n; mask++) {
      let sum = 0;
      let gastoCount = 0;
      let bigIngresoBonus = 0;
      for (let k = 0; k < n; k++) {
        const sign = mask & (1 << k) ? 1 : -1;
        const amt = partial[ambiguousIdx[k]!]!.row.amountCents;
        sum += sign * amt;
        if (sign < 0) gastoCount += 1;
        else if (amt >= 5_000_000) bigIngresoBonus += 1; // ingreso ≥ $50k → cobro grande
      }
      if (Math.abs(sum - required) <= 100) {
        const score = gastoCount + bigIngresoBonus * 0.5;
        if (score > bestScore) {
          bestScore = score;
          best = mask;
        }
      }
    }
    if (best !== null) {
      for (let k = 0; k < n; k++) {
        partial[ambiguousIdx[k]!]!.row.kind = best & (1 << k) ? "ingreso" : "gasto";
      }
    }
  };

  for (let i = 0; i < partial.length; i++) {
    const anchor = partial[i]!.pdfSaldoCents;
    if (anchor !== null) {
      settleBlock(i, anchor);
      lastAnchor = anchor;
      lastAnchorIdx = i;
    }
  }
}

/**
 * Códigos BBVA conocidos → kind. Los códigos son robustos y se imprimen
 * siempre al inicio del concepto. Si reconocemos el código, no necesitamos
 * adivinar por keywords ni por diff de saldos.
 *
 * Algunos códigos (N06, BNET) son ambiguos — pueden ser cargo o abono
 * según contexto. Para esos usamos heurística adicional con keywords y
 * saldo.
 */
const BBVA_CODE_KIND: Record<string, "ingreso" | "gasto" | null> = {
  T17: "gasto", // SPEI ENVIADO
  T20: "ingreso", // SPEI RECIBIDO
  T22: null, // SPEI VARIO (puede ser DEVUELTO entrante o saliente) — resolver por keyword
  P14: "gasto", // PAGO (KONFIO/SAT/etc.)
  P31: "gasto", // BANCO INVEX (pago al loan)
  P72: "gasto", // PARC 0X (parcialidad loan)
  A15: "gasto", // CARGO TARJETA (PAYU/DIGITALOCEAN/ADOBE)
  G30: "gasto", // RECIBO (luz, agua, etc.)
  S39: "gasto", // SERV BANCA
  S40: "gasto", // IVA COM
  N06: null, // PAGO CUENTA DE TERCERO — puede ser cargo o abono (resolver por contexto)
  BNET: null, // BNET — variable
  BIN_NET: null,
  B49: "ingreso", // BANCANET RECIBIDO
};

/**
 * Parsea una fila BBVA. Estrategia:
 *   1. amountCents = PRIMER número de la fila (columna CARGO/ABONO).
 *      Nunca derivar el monto del diff de saldos: cuando hay varias filas
 *      sin saldo en bloque, el diff acumulado se atribuye erróneamente
 *      a la última fila (bug histórico).
 *   2. kind = mapa de códigos BBVA. Para códigos ambiguos (N06, BNET)
 *      usar diff de saldos como desempate si está disponible y coincide
 *      con el primer número; si no, keywords; si no, default gasto.
 *   3. lastSaldoCents se actualiza usando el saldo de la fila si está,
 *      o se proyecta con (lastSaldo + signo × amount) si no.
 *
 * BBVA columnas: CARGO/ABONO + SALDO_OPER + SALDO_LIQ. Una fila completa
 * trae 3 montos. Cuando pdfjs pierde saldos solo se ve 1 número.
 */
function parseBBVAFullRow(
  date: string,
  fullText: string,
  prevSaldoCents: number | null,
): {
  row: BulkParsedRow;
  newSaldoCents: number | null;
  pdfSaldoCents: number | null;
  ambiguous: boolean;
} | null {
  const after = fullText.replace(/^\d{2}\/[A-Z]+\s+\d{2}\/[A-Z]+\s+/, "");
  const nums = [...after.matchAll(/(?<![\w])(\d{1,3}(?:,\d{3})*\.\d{2})/g)].map((m) => m[1]!);
  if (nums.length < 1) return null;

  const amountCents = parsePesos(nums[0]!);
  if (amountCents <= 0) return null;

  // El último número es saldo_liq si hay ≥2 números. Si hay exactamente 2,
  // el segundo puede ser saldo o un dato secundario (USD, AUT, TC). Solo lo
  // confiamos cuando coincide con la proyección del primer número.
  const codeMatch = after.match(/^([A-Z]\d{2}|BNET|BIN_NET)\b/);
  const rawCode = codeMatch?.[1];
  const codeKind = rawCode ? (BBVA_CODE_KIND[rawCode] ?? null) : null;
  let resolvedByDiff = false;

  const useKeywordKind = (): "ingreso" | "gasto" => {
    const lower = after.toLowerCase();
    if (
      /spei\s*recibido/i.test(lower) ||
      /dep[oó]sito/i.test(lower) ||
      /\babono\b/i.test(lower) ||
      /intereses\s+ganados/i.test(lower) ||
      /recibido/i.test(lower) ||
      // SPEI DEVUELTO: cuando un SPEI saliente es rechazado, el banco
      // regresa el dinero — es un INGRESO (cancela el cargo previo).
      /\bdevuelto\b/i.test(lower) ||
      /devoluci[oó]n/i.test(lower)
    ) {
      return "ingreso";
    }
    return "gasto";
  };

  let kind: "ingreso" | "gasto" = codeKind ?? useKeywordKind();

  // Para códigos ambiguos (N06, BNET, BIN_NET) intentamos resolver por diff
  // de saldos: si la fila tiene 3 números, comparamos saldo_nuevo con
  // (prevSaldo ± amount). El signo que cuadre nos da la dirección.
  if (codeKind === null && nums.length >= 3 && prevSaldoCents !== null) {
    const candidateSaldo = parsePesos(nums[nums.length - 1]!);
    const upPredicted = prevSaldoCents + amountCents;
    const downPredicted = prevSaldoCents - amountCents;
    if (Math.abs(candidateSaldo - upPredicted) <= 100) {
      kind = "ingreso";
      resolvedByDiff = true;
    } else if (Math.abs(candidateSaldo - downPredicted) <= 100) {
      kind = "gasto";
      resolvedByDiff = true;
    }
  }
  // Marca como ambigua si el código fue ambiguo Y no se pudo resolver por
  // diff: la 2da pasada en parseBBVAOficial intentará deducir el kind
  // usando saldos confirmados del PDF antes y después del bloque.
  const ambiguous = codeKind === null && !resolvedByDiff;

  // Proyectar el saldo nuevo a partir del kind. Si la fila trae saldo y
  // coincide con la proyección (±$1), usamos el del PDF; si no coincide
  // (común en filas con datos secundarios como USD/AUT), usamos la
  // proyección para mantener el tracking estable.
  // pdfSaldoCents: solo cuando el "último número" coincidió con la
  // proyección — eso es señal de que era realmente saldo. Usado en la
  // 2da pasada para resolver filas ambiguas con anclas confirmadas.
  const direction = kind === "ingreso" ? 1 : -1;
  const projected = prevSaldoCents !== null ? prevSaldoCents + direction * amountCents : null;
  let newSaldoCents: number | null = null;
  let pdfSaldoCents: number | null = null;
  if (nums.length >= 3) {
    // BBVA oficial: 3 números = CARGO/ABONO + SALDO_OPER + SALDO_LIQ.
    // Usamos SALDO_OPER (penúltimo) como ancla porque es el saldo después
    // del movimiento ordenado cronológicamente por fecha de OPERACIÓN
    // (que es como el parser ordena las filas). SALDO_LIQ (último) puede
    // ya descontar operaciones que aparecerán después en la lista (cuando
    // se operaron antes pero se liquidaron después).
    const oper = parsePesos(nums[nums.length - 2]!);
    const liq = parsePesos(nums[nums.length - 1]!);
    // Para tracking de lastSaldo usamos saldo_liq (cierre real). Para
    // ancla de la 2da pasada usamos saldo_oper.
    newSaldoCents = liq;
    pdfSaldoCents = oper;
  } else if (nums.length === 2) {
    // 2 números: el segundo puede ser saldo o un dato secundario (USD,
    // AUT, TC). Validamos con la proyección.
    const candidate = parsePesos(nums[nums.length - 1]!);
    if (projected === null || Math.abs(candidate - projected) <= 200) {
      newSaldoCents = candidate;
      pdfSaldoCents = candidate;
    } else if (projected !== null) {
      newSaldoCents = projected;
    }
  } else if (projected !== null) {
    newSaldoCents = projected;
  }

  // Detectar transferencia interna entre cuentas propias.
  let internalTransferHint: BulkParsedRow["internalTransferHint"];
  const propio =
    fullText.includes(RFC_PROPIO) ||
    /NUCLEO\s+OBSIDIANA/i.test(fullText) ||
    /KONFIO/i.test(fullText) ||
    /INBURSA/i.test(fullText) ||
    // BANCO INVEX es el fideicomisario de Konfio; los pagos al loan salen
    // como "BANCO INVEX SA FIDEI..." aunque la siguiente línea diga
    // "KONFIO LOAN ...". Detectarlo aquí asegura que el reconciliation
    // auto del loan agarre la fila aun si el parser perdió la 2da línea.
    /BANCO\s+INVEX/i.test(fullText) ||
    /FIDEI\d+/i.test(fullText) ||
    /^P14\s+KONFIO/i.test(after);
  if (propio) {
    internalTransferHint = {
      kind: kind === "gasto" ? "transfer_out" : "transfer_in",
      matchKey: `${date}-${amountCents}`,
    };
  }

  // Limpiar descripción: quitar código y montos.
  let description = after;
  if (rawCode) description = description.replace(new RegExp(`^${rawCode}\\s+`), "");
  for (const n of nums) description = description.replace(n, "");
  description = description.replace(/\s+/g, " ").trim().slice(0, 200);

  return {
    newSaldoCents,
    pdfSaldoCents,
    ambiguous,
    row: {
      date,
      description,
      kind,
      amountCents,
      ivaCents: 0,
      internalTransferHint,
      rawCounterparty: extractCounterparty(fullText),
      rawCode,
    },
  };
}

function extractCounterparty(fullText: string): string | undefined {
  // Para SPEI suele aparecer el nombre del beneficiario en mayúsculas en una
  // línea separada (ej. "JESUS ANTONIO SANCHEZ VAZQUEZ").
  const m = fullText.match(/\b([A-ZÁÉÍÓÚÑ]+(?:\s+[A-ZÁÉÍÓÚÑ]+){2,})\b/);
  if (m && !["BBVA", "MEXICO", "NUCLEO", "OBSIDIANA"].includes(m[1]!.split(" ")[0]!)) {
    return m[1];
  }
  return undefined;
}

function parsePesos(s: string): number {
  const n = parseFloat(s.replace(/,/g, ""));
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
}

/**
 * Parser para estados provisionales BBVA Empresas (web).
 * Estos los descarga el usuario antes de cierre de mes y tienen formato
 * distinto al estado oficial mensual:
 *   - Header: "Cuenta con / sin chequera - Mes actual - Mayo"
 *   - Filas: Día | Concepto/Referencia | Cargo | Abono | Saldo
 *   - Día solo es número (1-31), el mes se infiere del header
 *   - "Saldo disponible" arriba es el saldo más reciente
 */
function parseBBVAProvisional(text: string): BulkParseResult {
  const errors: string[] = [];
  const rows: BulkParsedRow[] = [];

  // Mes del header. "Mes actual - Mayo" o "Mes actual - mayo".
  const monthMatch = text.match(/Mes\s+actual\s*-\s*([A-Za-záéíóúñÑ]+)/i);
  // Año implícito: la fecha de impresión (top derecha) tiene formato DD/MM/YYYY.
  const printDateMatch = text.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  const month = monthMatch
    ? (MONTH_NAME_MAP[monthMatch[1]!.toLowerCase()] ??
      (printDateMatch ? parseInt(printDateMatch[2]!, 10) : new Date().getMonth() + 1))
    : printDateMatch
      ? parseInt(printDateMatch[2]!, 10)
      : new Date().getMonth() + 1;
  const year = printDateMatch ? parseInt(printDateMatch[3]!, 10) : new Date().getFullYear();

  const periodStart = `${year}-${String(month).padStart(2, "0")}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const periodEnd = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

  const accountMatch = text.match(/N[uú]mero\s+de\s+cuenta[\s\S]*?(\d{8,})/i);
  const accountHint = accountMatch?.[1];

  // Saldo disponible al momento de la consulta (es el más reciente).
  const saldoMatch = text.match(/Saldo\s+disponible[^\d-]*\$?\s*([-\d,]+\.\d{2})/i);
  const closingBalanceCents = saldoMatch ? parsePesos(saldoMatch[1]!) : undefined;

  // Filas: empiezan con DD (1-2 dígitos) seguido de concepto. El extractor
  // separa cada fila en una o más líneas. Identificamos inicio de fila por
  // un día de 1-2 dígitos al inicio de línea, seguido de letras o "$".
  const lines = text
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  // Refactor: detectar cada fila como una línea con formato
  //   "DD [concepto?] $monto $saldo"
  // Caso real (extractor por Y):
  //   [8] SPEI ENVIADO BANREGIO/0035817478 058     ← descripción (línea -1)
  //   [9] 08 $ 81,200.00 $ 8,535.96                ← fila central
  //   [10] 0805260Nucleo Obsidiana                  ← referencia (línea +1)
  // La descripción del concepto está en la línea ANTERIOR a la fila
  // central. La referencia en la SIGUIENTE.
  const moneyLineRe = /^\s*(\d{1,2})\s+(.*?)\$\s*([\d,]+\.\d{2})\s+\$\s*([\d,]+\.\d{2})\s*$/;

  // Detectar dónde empieza y termina la tabla.
  let tableStart = -1;
  let tableEnd = lines.length;
  for (let i = 0; i < lines.length; i++) {
    if (
      tableStart < 0 &&
      (/D[ií]a\s+Concepto/i.test(lines[i]!) || /Concepto\s*\/\s*Referencia/i.test(lines[i]!))
    ) {
      tableStart = i + 1;
    }
    if (
      tableStart >= 0 &&
      (/En\s+cumplimiento\s+a\s+la\s+Ley/i.test(lines[i]!) || /^Regresar/i.test(lines[i]!))
    ) {
      tableEnd = i;
      break;
    }
  }

  for (let i = tableStart; i < tableEnd && i >= 0; i++) {
    const line = lines[i]!;
    const m = line.match(moneyLineRe);
    if (!m) continue;
    const day = parseInt(m[1]!, 10);
    if (day < 1 || day > 31) continue;
    const inlineConcept = m[2]!.trim();
    const amount = parsePesos(m[3]!);
    if (amount <= 0) continue;
    // Descripción: concepto inline (si existe), línea previa (si no es
    // otra fila), y línea siguiente (referencia). Filtramos líneas que
    // empiecen con DD (otra fila) o ruido conocido.
    const isContextLine = (l?: string) =>
      l != null && l.length > 0 && !moneyLineRe.test(l) && !/^D[ií]a/i.test(l);
    // Siempre intentamos juntar descripción de las líneas adyacentes.
    // La línea anterior suele tener el concepto principal ("SPEI RECIBIDO
    // SANTANDER"). La siguiente, la referencia ("0448197STRIPE").
    const parts: string[] = [];
    if (isContextLine(lines[i - 1])) parts.push(lines[i - 1]!);
    if (inlineConcept && !/^\d+$/.test(inlineConcept)) parts.push(inlineConcept);
    if (isContextLine(lines[i + 1])) parts.push(lines[i + 1]!);
    const fullText = parts.join(" ").trim();
    const parsed = parseBBVAProvisionalRow(year, month, day, fullText, amount);
    if (parsed) rows.push(parsed);
  }

  return {
    bank: "bbva",
    accountHint,
    periodStart,
    periodEnd,
    closingBalanceCents,
    rows,
    errors,
  };
}

function parseBBVAProvisionalRow(
  year: number,
  month: number,
  day: number,
  fullText: string,
  explicitAmount?: number,
): BulkParsedRow | null {
  const nums = [...fullText.matchAll(/\$?\s*(\d{1,3}(?:,\d{3})*\.\d{2})/g)].map((m) => m[1]!);
  const movementAmount = explicitAmount ?? (nums.length >= 1 ? parsePesos(nums[0]!) : 0);
  if (movementAmount <= 0) return null;

  // Determinar cargo/abono por keyword. Importante: chequear SPEI ENVIADO
  // ANTES que SPEI RECIBIDO, porque la descripción puede tener ambas
  // (cuando una fila tiene una referencia de SPEI ENVIADO seguido de
  // un SPEI RECIBIDO en el contexto, ej. orden cronológico inverso).
  // Para evitar el bug, partimos del primer keyword fuerte que matchee
  // en orden de aparición en el texto.
  let kind: "ingreso" | "gasto" = "gasto";
  const sendIdx = fullText.search(/SPEI\s*ENVIADO|TRASPASO\s*SPEI|PAGO\s+VIA\s+SPEI/i);
  const recvIdx = fullText.search(/SPEI\s*RECIBIDO|DEP[OÓ]SITO|ABONO|INTERESES\s+GANADOS/i);
  if (sendIdx >= 0 && (recvIdx < 0 || sendIdx < recvIdx)) {
    kind = "gasto"; // outgoing aparece primero → es esta fila
  } else if (recvIdx >= 0) {
    kind = "ingreso";
  } else if (
    /IVA\s+COM|SERV\s+BANCA|BANCO\s+INVEX|KONFIO\s+LOAN|PAYU|DIGITALOCEAN|RECIBO\s+NO/i.test(
      fullText,
    )
  ) {
    kind = "gasto";
  }

  // Detectar transfer interno entre cuentas propias.
  let internalTransferHint: BulkParsedRow["internalTransferHint"];
  const propio =
    fullText.includes(RFC_PROPIO) ||
    /NUCLEO\s+OBSIDIANA/i.test(fullText) ||
    /KONFIO/i.test(fullText) ||
    /INBURSA/i.test(fullText) ||
    /transferencia\s+entre\s+cuentas/i.test(fullText);
  if (propio) {
    internalTransferHint = {
      kind: kind === "gasto" ? "transfer_out" : "transfer_in",
      matchKey: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}-${movementAmount}`,
    };
  }

  // Limpiar descripción: quita los montos y deja solo concepto.
  let description = fullText;
  for (const n of nums) description = description.replace(n, "");
  description = description.replace(/\$/g, "").replace(/\s+/g, " ").trim().slice(0, 200);

  return {
    date: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
    description,
    kind,
    amountCents: movementAmount,
    ivaCents: 0,
    internalTransferHint,
    rawCounterparty: extractCounterparty(fullText),
  };
}
